<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useDealsStore } from 'dashboard/stores/deals';
import { useAlert } from 'dashboard/composables';
import CommercialImportDialog from '../components/CommercialImportDialog.vue';

const store = useDealsStore();
const router = useRouter();
const { t } = useI18n();

const filters = reactive({
  q: '',
  assignedUserId: '',
  companyId: '',
  dateFrom: '',
  dateTo: '',
  status: '',
});
const createOpen = ref(false);
const importOpen = ref(false);
const quickMenuDealId = ref(null);
const lossDialog = reactive({ open: false, deal: null, stage: null });
const winDialog = reactive({ open: false, deal: null, stage: null });
const dragOverStageId = ref(null);
const lossReasonId = ref('');
const newLossReason = ref('');
const similarDeals = ref([]);
const form = reactive({
  name: '',
  companyId: '',
  assignedUserId: '',
  amount: 0,
  currency: 'COP',
  probability: 0,
  expectedCloseDate: '',
  source: '',
  businessLine: '',
});

const money = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'COP',
  maximumFractionDigits: 0,
});

const dealsForStage = stageId =>
  store.deals.filter(deal => deal.currentStageId === stageId);
const stageAmount = stageId =>
  dealsForStage(stageId).reduce(
    (total, deal) => total + Number(deal.amount),
    0
  );
const stageCount = stageId => dealsForStage(stageId).length;
const stageLabel = stage => stage.name;
const activityLabel = deal => {
  if (deal.overdueActivitiesCount) {
    return t('COMMERCIAL.OVERDUE_ACTIVITIES', {
      count: deal.overdueActivitiesCount,
    });
  }
  if (deal.pendingActivitiesCount) {
    return t('COMMERCIAL.PENDING_ACTIVITIES', {
      count: deal.pendingActivitiesCount,
    });
  }
  return t('COMMERCIAL.NO_PENDING_ACTIVITIES');
};

const loadPipeline = async () => {
  try {
    await store.fetchPipeline(filters);
  } catch {
    useAlert(t('COMMERCIAL.MESSAGES.LOAD_ERROR'));
  }
};

let filterTimer;
watch(
  filters,
  () => {
    window.clearTimeout(filterTimer);
    filterTimer = window.setTimeout(loadPipeline, 300);
  },
  { deep: true }
);

let similarTimer;
watch(
  () => [form.companyId, form.name],
  () => {
    window.clearTimeout(similarTimer);
    similarTimer = window.setTimeout(async () => {
      similarDeals.value = await store.findSimilar(form.companyId, form.name);
    }, 350);
  }
);

const resetForm = () => {
  Object.assign(form, {
    name: '',
    companyId: '',
    assignedUserId: '',
    amount: 0,
    currency: 'COP',
    probability: 0,
    expectedCloseDate: '',
    source: '',
    businessLine: '',
  });
  similarDeals.value = [];
};

const createDeal = async () => {
  try {
    await store.createDeal(form);
    createOpen.value = false;
    resetForm();
    useAlert(t('COMMERCIAL.MESSAGES.CREATED'));
  } catch {
    useAlert(t('COMMERCIAL.MESSAGES.CREATE_ERROR'));
  }
};

const moveDeal = async (deal, stage, lostReason = null) => {
  try {
    await store.moveDeal(deal.id, stage.id, lostReason?.id);
  } catch {
    useAlert(t('COMMERCIAL.MESSAGES.MOVE_ERROR'));
    await loadPipeline();
  }
};

const handleAdded = (event, stage) => {
  dragOverStageId.value = null;
  const deal = event.added?.element;
  if (!deal || deal.currentStageId === stage.id) return;
  if (!stage.allowDragDrop) {
    loadPipeline();
    return;
  }
  if (
    stage.stageType === 'lost' &&
    (stage.requiresConfirmation || stage.requiresLossReason)
  ) {
    Object.assign(lossDialog, { open: true, deal, stage });
    return;
  }
  if (stage.stageType === 'won' && stage.requiresConfirmation) {
    Object.assign(winDialog, { open: true, deal, stage });
    return;
  }
  moveDeal(deal, stage);
};

const confirmWin = async () => {
  await moveDeal(winDialog.deal, winDialog.stage);
  Object.assign(winDialog, { open: false, deal: null, stage: null });
};

const confirmLoss = async () => {
  let reason = store.lossReasons.find(
    item => item.id === Number(lossReasonId.value)
  );
  if (!reason && newLossReason.value.trim()) {
    reason = await store.createLossReason(newLossReason.value.trim());
  }
  if (lossDialog.stage.requiresLossReason && !reason) {
    useAlert(t('COMMERCIAL.MESSAGES.LOSS_REASON_REQUIRED'));
    return;
  }
  await moveDeal(lossDialog.deal, lossDialog.stage, reason);
  Object.assign(lossDialog, { open: false, deal: null, stage: null });
  lossReasonId.value = '';
  newLossReason.value = '';
};

const openDeal = deal =>
  router.push({ name: 'deal_detail', params: { dealId: deal.id } });

const quickActions = computed(() => [
  [
    'activity',
    'i-lucide-calendar-plus',
    t('COMMERCIAL.QUICK_ACTIONS.ACTIVITY'),
  ],
  [
    'whatsapp',
    'i-lucide-message-circle',
    t('COMMERCIAL.QUICK_ACTIONS.WHATSAPP'),
  ],
  [
    'comment',
    'i-lucide-message-square-plus',
    t('COMMERCIAL.QUICK_ACTIONS.COMMENT'),
  ],
  ['email', 'i-lucide-mail-plus', t('COMMERCIAL.QUICK_ACTIONS.EMAIL')],
  ['document', 'i-lucide-paperclip', t('COMMERCIAL.QUICK_ACTIONS.DOCUMENT')],
  ['quotation', 'i-lucide-file-text', t('COMMERCIAL.QUICK_ACTIONS.QUOTATION')],
]);

const openQuickAction = (deal, action) => {
  quickMenuDealId.value = null;
  router.push({
    name: 'deal_detail',
    params: { dealId: deal.id },
    query: { action },
  });
};

onMounted(loadPipeline);
</script>

<template>
  <main class="flex flex-col h-full min-w-0 bg-n-background">
    <header
      class="flex flex-wrap gap-3 items-center justify-between px-6 py-4 border-b border-n-weak"
    >
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ t('COMMERCIAL.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-10">{{ t('COMMERCIAL.SUBTITLE') }}</p>
      </div>
      <div class="flex gap-2">
        <button
          class="rounded-lg border border-n-weak px-4 py-2 text-sm font-medium text-n-slate-12"
          type="button"
          @click="importOpen = true"
        >
          {{ t('COMMERCIAL.ACTIONS.IMPORT') }}
        </button>
        <button
          class="rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white hover:opacity-90"
          type="button"
          @click="createOpen = true"
        >
          {{ t('COMMERCIAL.ACTIONS.NEW_DEAL') }}
        </button>
      </div>
    </header>

    <section
      class="grid grid-cols-2 gap-2 px-6 py-3 border-b md:grid-cols-6 border-n-weak"
    >
      <input
        v-model="filters.q"
        class="px-3 h-9 text-sm rounded-lg border bg-n-alpha-1 border-n-weak"
        :placeholder="t('COMMERCIAL.FILTERS.SEARCH')"
      />
      <select
        v-model="filters.assignedUserId"
        class="px-2 h-9 text-sm rounded-lg border bg-n-alpha-1 border-n-weak"
      >
        <option value="">{{ t('COMMERCIAL.FILTERS.ALL_ADVISORS') }}</option>
        <option v-for="agent in store.agents" :key="agent.id" :value="agent.id">
          {{ agent.name }}
        </option>
      </select>
      <select
        v-model="filters.companyId"
        class="px-2 h-9 text-sm rounded-lg border bg-n-alpha-1 border-n-weak"
      >
        <option value="">{{ t('COMMERCIAL.FILTERS.ALL_COMPANIES') }}</option>
        <option
          v-for="company in store.companies"
          :key="company.id"
          :value="company.id"
        >
          {{ company.name }}
        </option>
      </select>
      <select
        v-model="filters.status"
        class="px-2 h-9 text-sm rounded-lg border bg-n-alpha-1 border-n-weak"
      >
        <option value="">{{ t('COMMERCIAL.FILTERS.ALL_STATUSES') }}</option>
        <option value="open">{{ t('COMMERCIAL.STATUS.OPEN') }}</option>
        <option value="won">{{ t('COMMERCIAL.STATUS.WON') }}</option>
        <option value="lost">{{ t('COMMERCIAL.STATUS.LOST') }}</option>
      </select>
      <input
        v-model="filters.dateFrom"
        type="date"
        class="px-2 h-9 text-sm rounded-lg border bg-n-alpha-1 border-n-weak"
        :aria-label="t('COMMERCIAL.FILTERS.DATE_FROM')"
      />
      <input
        v-model="filters.dateTo"
        type="date"
        class="px-2 h-9 text-sm rounded-lg border bg-n-alpha-1 border-n-weak"
        :aria-label="t('COMMERCIAL.FILTERS.DATE_TO')"
      />
    </section>

    <section class="flex min-h-0 flex-1 gap-3 overflow-x-auto bg-n-alpha-1 p-4">
      <article
        v-for="stage in store.stages"
        :key="stage.id"
        class="flex w-80 flex-none flex-col overflow-hidden rounded-xl border bg-n-alpha-2 shadow-sm transition-colors"
        :class="
          dragOverStageId === stage.id
            ? 'border-n-brand ring-2 ring-n-brand/30'
            : 'border-n-weak'
        "
        @dragenter.prevent="dragOverStageId = stage.id"
        @dragover.prevent
      >
        <header class="border-b border-n-weak bg-n-background/80 px-4 py-3">
          <div class="flex items-start justify-between gap-3">
            <div class="flex min-w-0 items-center gap-2">
              <input
                :value="stage.color"
                type="color"
                disabled
                class="size-4 shrink-0 border-0 bg-transparent p-0"
              />
              <h2 class="min-w-0 text-sm font-semibold text-n-slate-12">
                {{ stageLabel(stage) }}
              </h2>
            </div>
            <span
              class="shrink-0 rounded-full border border-n-weak bg-n-alpha-2 px-2 py-0.5 text-xs font-medium text-n-slate-11"
            >
              {{ stageCount(stage.id) }}
            </span>
          </div>
          <p class="mt-2 text-base font-semibold text-n-slate-12">
            {{ money.format(stageAmount(stage.id)) }}
          </p>
          <p class="mt-0.5 text-xs text-n-slate-9">
            {{ t('COMMERCIAL.DEALS_COUNT', { count: stageCount(stage.id) }) }}
          </p>
        </header>
        <Draggable
          :list="dealsForStage(stage.id)"
          :disabled="!stage.allowDragDrop"
          item-key="id"
          group="deals"
          class="flex min-h-24 flex-1 flex-col gap-3 overflow-y-auto p-3"
          ghost-class="opacity-40"
          @start="dragOverStageId = stage.id"
          @end="dragOverStageId = null"
          @change="handleAdded($event, stage)"
        >
          <template #item="{ element: deal }">
            <article
              role="button"
              tabindex="0"
              class="group relative rounded-xl border border-n-weak bg-n-background p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-n-brand hover:shadow-lg"
              @click="openDeal(deal)"
              @keydown.enter="openDeal(deal)"
            >
              <div class="flex items-start justify-between gap-2">
                <strong
                  class="block min-w-0 text-sm font-semibold leading-5 text-n-slate-12"
                >
                  {{ deal.name }}
                </strong>
                <button
                  type="button"
                  class="shrink-0 rounded-md p-1.5 text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-slate-12"
                  :aria-label="t('COMMERCIAL.ACTIONS.QUICK_ACTIONS')"
                  @click.stop="
                    quickMenuDealId =
                      quickMenuDealId === deal.id ? null : deal.id
                  "
                >
                  <span class="i-lucide-ellipsis size-4" />
                </button>
              </div>
              <div
                v-if="quickMenuDealId === deal.id"
                class="absolute end-3 top-11 z-20 w-52 overflow-hidden rounded-xl border border-n-weak bg-n-background shadow-2xl"
                @click.stop
              >
                <p
                  class="border-b border-n-weak px-3 py-2 text-xs font-semibold uppercase tracking-wide text-n-slate-9"
                >
                  {{ t('COMMERCIAL.ACTIONS.QUICK_ACTIONS') }}
                </p>
                <button
                  v-for="action in quickActions"
                  :key="action[0]"
                  type="button"
                  class="flex w-full items-center gap-3 px-3 py-2.5 text-sm text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                  @click="openQuickAction(deal, action[0])"
                >
                  <span
                    class="grid size-7 place-content-center rounded-md bg-n-alpha-2 text-n-brand"
                  >
                    <span class="size-4" :class="[action[1]]" />
                  </span>
                  {{ action[2] }}
                </button>
              </div>
              <div
                class="mt-2 flex items-center gap-1.5 text-xs text-n-slate-10"
              >
                <span class="i-lucide-building-2 size-3.5 shrink-0" />
                <span class="truncate">{{ deal.company.name }}</span>
              </div>
              <div
                v-if="deal.contact"
                class="mt-1.5 flex items-center gap-1.5 text-xs text-n-slate-10"
              >
                <span class="i-lucide-user-round size-3.5 shrink-0" />
                <span class="truncate">{{ deal.contact.name }}</span>
              </div>
              <strong class="mt-3 block text-base text-n-slate-12">
                {{ money.format(Number(deal.amount)) }}
              </strong>
              <div
                class="mt-3 flex items-center justify-between gap-2 border-t border-n-weak pt-3 text-xs text-n-slate-10"
              >
                <span class="flex min-w-0 items-center gap-1.5">
                  <span class="i-lucide-user-check size-3.5 shrink-0" />
                  <span class="truncate">{{
                    deal.assignedUser?.name || t('COMMERCIAL.UNASSIGNED')
                  }}</span>
                </span>
                <span class="shrink-0">{{
                  t('COMMERCIAL.DAYS_IN_STAGE', { count: deal.daysInStage })
                }}</span>
              </div>
              <div class="mt-2 flex flex-wrap items-center gap-2">
                <span
                  class="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs"
                  :class="
                    deal.overdueActivitiesCount
                      ? 'bg-n-ruby-3 text-n-ruby-11'
                      : deal.pendingActivitiesCount
                        ? 'bg-n-amber-3 text-n-amber-11'
                        : 'bg-n-alpha-2 text-n-slate-9'
                  "
                >
                  <span
                    class="size-3.5"
                    :class="
                      deal.overdueActivitiesCount
                        ? 'i-lucide-circle-alert'
                        : 'i-lucide-circle-check'
                    "
                  />
                  {{ activityLabel(deal) }}
                </span>
                <span
                  class="inline-flex items-center gap-1 text-xs"
                  :class="
                    deal.inactivityDays >= 7
                      ? 'text-n-ruby-11'
                      : 'text-n-slate-9'
                  "
                >
                  <span class="i-lucide-clock-3 size-3.5" />
                  {{
                    t('COMMERCIAL.INACTIVITY_DAYS', {
                      count: deal.inactivityDays,
                    })
                  }}
                </span>
              </div>
            </article>
          </template>
        </Draggable>
      </article>
      <p v-if="store.isLoading" class="p-4 text-sm text-n-slate-10">
        {{ t('COMMERCIAL.LOADING') }}
      </p>
    </section>

    <div
      v-if="createOpen"
      class="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black/50"
      @click.self="createOpen = false"
    >
      <form
        class="p-5 w-full max-w-xl rounded-xl border shadow-xl bg-n-background border-n-weak"
        @submit.prevent="createDeal"
      >
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ t('COMMERCIAL.CREATE.TITLE') }}
        </h2>
        <div class="grid grid-cols-2 gap-3 mt-4">
          <label class="col-span-2 text-sm text-n-slate-11">
            {{ t('COMMERCIAL.FIELDS.NAME') }}
            <input
              v-model="form.name"
              required
              class="block px-3 mt-1 w-full h-9 rounded-lg border bg-n-alpha-1 border-n-weak"
            />
          </label>
          <label class="col-span-2 text-sm text-n-slate-11">
            {{ t('COMMERCIAL.FIELDS.COMPANY') }}
            <select
              v-model="form.companyId"
              required
              class="block px-2 mt-1 w-full h-9 rounded-lg border bg-n-alpha-1 border-n-weak"
            >
              <option value="" disabled>
                {{ t('COMMERCIAL.FIELDS.SELECT_COMPANY') }}
              </option>
              <option
                v-for="company in store.companies"
                :key="company.id"
                :value="company.id"
              >
                {{ company.name }}
              </option>
            </select>
          </label>
          <div
            v-if="similarDeals.length"
            class="col-span-2 p-3 text-sm rounded-lg border border-amber-300 bg-amber-50 text-amber-900"
          >
            {{
              t('COMMERCIAL.SIMILAR_WARNING', { count: similarDeals.length })
            }}
          </div>
          <label class="text-sm text-n-slate-11">
            {{ t('COMMERCIAL.FIELDS.AMOUNT') }}
            <input
              v-model.number="form.amount"
              min="0"
              type="number"
              class="block px-3 mt-1 w-full h-9 rounded-lg border bg-n-alpha-1 border-n-weak"
            />
          </label>
          <label class="text-sm text-n-slate-11">
            {{ t('COMMERCIAL.FIELDS.CURRENCY') }}
            <input
              v-model="form.currency"
              maxlength="3"
              required
              class="block px-3 mt-1 w-full h-9 rounded-lg border bg-n-alpha-1 border-n-weak"
            />
          </label>
          <label class="text-sm text-n-slate-11">
            {{ t('COMMERCIAL.FIELDS.ADVISOR') }}
            <select
              v-model="form.assignedUserId"
              class="block px-2 mt-1 w-full h-9 rounded-lg border bg-n-alpha-1 border-n-weak"
            >
              <option value="">{{ t('COMMERCIAL.UNASSIGNED') }}</option>
              <option
                v-for="agent in store.agents"
                :key="agent.id"
                :value="agent.id"
              >
                {{ agent.name }}
              </option>
            </select>
          </label>
          <label class="text-sm text-n-slate-11">
            {{ t('COMMERCIAL.FIELDS.CLOSE_DATE') }}
            <input
              v-model="form.expectedCloseDate"
              type="date"
              class="block px-2 mt-1 w-full h-9 rounded-lg border bg-n-alpha-1 border-n-weak"
            />
          </label>
        </div>
        <div class="flex gap-2 justify-end mt-5">
          <button
            type="button"
            class="px-4 py-2 text-sm rounded-lg border border-n-weak"
            @click="createOpen = false"
          >
            {{ t('COMMERCIAL.ACTIONS.CANCEL') }}
          </button>
          <button
            type="submit"
            :disabled="store.isSaving"
            class="px-4 py-2 text-sm text-white rounded-lg bg-n-brand disabled:opacity-50"
          >
            {{ t('COMMERCIAL.ACTIONS.CREATE') }}
          </button>
        </div>
      </form>
    </div>
    <CommercialImportDialog
      :show="importOpen"
      @close="importOpen = false"
      @imported="loadPipeline"
    />

    <div
      v-if="winDialog.open"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      @click.self="
        winDialog.open = false;
        loadPipeline();
      "
    >
      <div
        class="w-full max-w-md rounded-xl border border-n-weak bg-n-background p-5 shadow-xl"
      >
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ t('COMMERCIAL.WIN.TITLE') }}
        </h2>
        <p class="mt-2 text-sm text-n-slate-10">
          {{ t('COMMERCIAL.WIN.DESCRIPTION') }}
        </p>
        <div class="mt-5 flex justify-end gap-2">
          <button
            type="button"
            class="rounded-lg border border-n-weak px-4 py-2 text-sm"
            @click="
              winDialog.open = false;
              loadPipeline();
            "
          >
            {{ t('COMMERCIAL.ACTIONS.CANCEL') }}
          </button>
          <button
            type="button"
            class="rounded-lg bg-n-teal-9 px-4 py-2 text-sm font-medium text-white"
            @click="confirmWin"
          >
            {{ t('COMMERCIAL.WIN.CONFIRM') }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="lossDialog.open"
      class="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black/50"
    >
      <div
        class="p-5 w-full max-w-md rounded-xl border shadow-xl bg-n-background border-n-weak"
      >
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ t('COMMERCIAL.LOSS.TITLE') }}
        </h2>
        <select
          v-model="lossReasonId"
          class="px-2 mt-4 w-full h-9 rounded-lg border bg-n-alpha-1 border-n-weak"
        >
          <option value="">{{ t('COMMERCIAL.LOSS.SELECT_REASON') }}</option>
          <option
            v-for="reason in store.lossReasons"
            :key="reason.id"
            :value="reason.id"
          >
            {{ reason.name }}
          </option>
        </select>
        <input
          v-model="newLossReason"
          class="px-3 mt-3 w-full h-9 rounded-lg border bg-n-alpha-1 border-n-weak"
          :placeholder="t('COMMERCIAL.LOSS.NEW_REASON')"
        />
        <div class="flex gap-2 justify-end mt-5">
          <button
            type="button"
            class="px-4 py-2 text-sm rounded-lg border border-n-weak"
            @click="
              lossDialog.open = false;
              loadPipeline();
            "
          >
            {{ t('COMMERCIAL.ACTIONS.CANCEL') }}
          </button>
          <button
            type="button"
            class="px-4 py-2 text-sm text-white rounded-lg bg-red-600"
            @click="confirmLoss"
          >
            {{ t('COMMERCIAL.LOSS.CONFIRM') }}
          </button>
        </div>
      </div>
    </div>
  </main>
</template>
